import { useMemo, useState } from "react";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import {
  currentRsvpSummary,
  formatCountdownHours,
  normalizeSurveyRows,
  pollNeedsResponse,
  surveyTypeLabel,
} from "../../../utils/clientSessionUtils";
import AttendancePollButtons from "./AttendancePollButtons";
import s from "./clientApp.module.css";

function sessionFromSurveyRow(row) {
  if (!row?.session) return null;
  return {
    id: row.session.id,
    session_id: row.session.id,
    start_datetime: row.session.start_datetime,
    end_datetime: row.session.end_datetime,
    session_kind: "training",
    source: "training_session",
    group: row.session.group,
  };
}

function AttendanceSurveyCard({ row, onDone }) {
  const [phase, setPhase] = useState("choose");
  const session = sessionFromSurveyRow(row);
  const answered = Boolean(row.responded_at) || Boolean(currentRsvpSummary(session, row));
  const countdown = formatCountdownHours(row.hours_until_session);
  const groupLabel = row.session?.group?.name || "Тренировка";

  return (
    <article
      className={`${s.homeSurveyCard} ${s.homeSurveyCardAttendance} ${
        answered ? s.homeSurveyCardAnswered : ""
      }`}
    >
      <header className={s.homeSurveyCardHead}>
        <span className={s.homeSurveyTypeBadge}>
          {surveyTypeLabel("attendance")}
        </span>
        {countdown && !answered ? (
          <span className={s.homeSurveyTimer}>{countdown}</span>
        ) : null}
      </header>
      <h3 className={s.homeSurveyTitle}>{groupLabel}</h3>
      <p className={s.homeSurveyMeta}>
        {session
          ? `${formatDateRu(session.start_datetime, {
              weekday: "short",
              day: "numeric",
              month: "short",
            })} · ${formatTimeRu(session.start_datetime)}`
          : null}
      </p>
      {session ? (
        <AttendancePollButtons
          session={session}
          pollRow={row}
          allowDirect
          variant="sheet"
          question={row.poll?.questions?.[0]?.text || "Подтвердите явку"}
          onPhaseChange={setPhase}
          onDone={onDone}
        />
      ) : null}
      {answered && phase === "answered" ? (
        <p className={s.homeSurveyAnsweredHint}>Отвечено</p>
      ) : null}
    </article>
  );
}

function FeedbackSurveyCard({ row, onDone }) {
  const answered = Boolean(row.responded_at);
  const type = row.survey_type || row.poll?.poll_type;

  return (
    <article
      className={`${s.homeSurveyCard} ${s.homeSurveyCardFeedback} ${
        answered ? s.homeSurveyCardAnswered : ""
      }`}
    >
      <header className={s.homeSurveyCardHead}>
        <span className={s.homeSurveyTypeBadge}>{surveyTypeLabel(type)}</span>
      </header>
      <h3 className={s.homeSurveyTitle}>{row.poll?.title || "Опрос"}</h3>
      {row.poll?.description ? (
        <p className={s.homeSurveyMeta}>{row.poll.description}</p>
      ) : null}
      {!answered && pollNeedsResponse(row) ? (
        <AttendancePollButtons
          session={sessionFromSurveyRow(row)}
          pollRow={row}
          allowDirect={false}
          variant="sheet"
          question={row.poll?.questions?.[0]?.text}
          onDone={onDone}
        />
      ) : (
        <p className={s.homeSurveyAnsweredHint}>Спасибо за ответ</p>
      )}
    </article>
  );
}

export default function ClientSurveyFeed({ sections, onDone }) {
  const attendance = useMemo(
    () => normalizeSurveyRows(sections?.attendance || []),
    [sections],
  );
  const feedback = useMemo(
    () => normalizeSurveyRows(sections?.feedback || []),
    [sections],
  );
  const custom = useMemo(
    () => normalizeSurveyRows(sections?.custom || []),
    [sections],
  );

  const hasAny =
    attendance.length > 0 || feedback.length > 0 || custom.length > 0;
  if (!hasAny) return null;

  return (
    <section className={s.homeSurveyZone} aria-label="Опросы и подтверждения">
      <h2 className={s.homeSurveyZoneTitle}>Требует ответа</h2>
      <div className={s.homeSurveyList}>
        {attendance.map((row) => (
          <AttendanceSurveyCard
            key={`att-${row.poll_id}-${row.session_id}`}
            row={row}
            onDone={onDone}
          />
        ))}
        {feedback.map((row) => (
          <FeedbackSurveyCard
            key={`fb-${row.id}`}
            row={row}
            onDone={onDone}
          />
        ))}
        {custom.map((row) => (
          <FeedbackSurveyCard
            key={`cu-${row.id}`}
            row={row}
            onDone={onDone}
          />
        ))}
      </div>
    </section>
  );
}
